import type { WorkspaceRow } from '@/types/api'
import { api } from '@/lib/api'
import {
  TICKETS_DEFAULT_SORT_BY,
  TICKETS_DEFAULT_SORT_ORDER,
  type TicketsSortOrder,
} from '@/lib/tickets-list-sort'

export type WorkspaceTicketsSortBy = 'updated_at' | 'weight'

export type WorkspaceTicketsSortDefaults = {
  sortBy: WorkspaceTicketsSortBy
  sortOrder: TicketsSortOrder
}

export const WORKSPACE_TICKETS_SORT_DEFAULTS_CHANGED = 'workspace-tickets-sort-defaults-changed'

const LS_KEY = 'servicedesk.workspace.ticketsSortDefaults'

export const FALLBACK_WORKSPACE_TICKETS_SORT_DEFAULTS: WorkspaceTicketsSortDefaults = {
  sortBy: TICKETS_DEFAULT_SORT_BY,
  sortOrder: TICKETS_DEFAULT_SORT_ORDER,
}

function normalizeSortBy(raw: unknown): WorkspaceTicketsSortBy {
  return raw === 'weight' ? 'weight' : 'updated_at'
}

function normalizeSortOrder(raw: unknown): TicketsSortOrder {
  if (raw === 'newest') return 'newest'
  return raw === 'desc' ? 'desc' : 'asc'
}

export function workspaceTicketsSortDefaultsFromRow(
  ws: Pick<WorkspaceRow, 'tickets_default_sort_by' | 'tickets_default_sort_order'> | null | undefined,
): WorkspaceTicketsSortDefaults {
  if (!ws) return FALLBACK_WORKSPACE_TICKETS_SORT_DEFAULTS
  return {
    sortBy: normalizeSortBy(ws.tickets_default_sort_by),
    sortOrder: normalizeSortOrder(ws.tickets_default_sort_order),
  }
}

export function readCachedWorkspaceTicketsSortDefaults(): WorkspaceTicketsSortDefaults {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return FALLBACK_WORKSPACE_TICKETS_SORT_DEFAULTS
    const parsed = JSON.parse(raw) as Partial<WorkspaceTicketsSortDefaults>
    return {
      sortBy: normalizeSortBy(parsed.sortBy),
      sortOrder: normalizeSortOrder(parsed.sortOrder),
    }
  } catch {
    return FALLBACK_WORKSPACE_TICKETS_SORT_DEFAULTS
  }
}

export function writeCachedWorkspaceTicketsSortDefaults(defaults: WorkspaceTicketsSortDefaults): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(defaults))
  } catch {
    /* ignore quota / private mode */
  }
  window.dispatchEvent(new CustomEvent(WORKSPACE_TICKETS_SORT_DEFAULTS_CHANGED))
}

export function applyWorkspaceTicketsSortDefaultsFromRow(ws: WorkspaceRow): WorkspaceTicketsSortDefaults {
  const defaults = workspaceTicketsSortDefaultsFromRow(ws)
  writeCachedWorkspaceTicketsSortDefaults(defaults)
  return defaults
}

export async function refreshWorkspaceTicketsSortDefaultsFromApi(): Promise<WorkspaceTicketsSortDefaults> {
  const ws = await api.workspaceMe()
  return applyWorkspaceTicketsSortDefaultsFromRow(ws)
}
